import EffectiveRecordMedia from "../workspace/EffectiveRecordMedia";
import CourseThemePreview from './CourseThemePreview';
import { COURSE_TEMPLATE_NAMES } from 'lib/coursePresentation';
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminRequest } from "lib/adminSiteApi";
import { notifyCourseContentChanged } from 'lib/courseContentChanges';
import { useAdmin, hasCapability } from "../RequireAdmin";
import {
  ActionForm,
  Fields,
  Loading,
  Record,
  cleanDraft,
  useRemote,
  label,
} from "../workspace/Controls";
import { RelatedRecords, SimpleAction } from "../workspace/Records";
import Upload from "../workspace/Upload";
import { useContentRecords } from "./ContentLibrary";
const SPEAKER_GROUPS = [
  ["Identity", "name displayName slug title company location"],
  ["Biography", "headline quote shortBio fullBio bio"],
  [
    "Profile sections",
    "credentials specialties trainingFocus languages previousExperience workHistory",
  ],
  ["Links", "linkedInUrl websiteUrl"],
];
const COURSE_GROUPS = [
  [
    "Course identity",
    "title slug category kind isDemo number webinarNumber anchor",
  ],
  [
    "Course content",
    "description audience takeaway level covered agenda outcomes",
  ],
  ["Curriculum", "modules"],
  [
    "Public preview",
    "previewVideoEnabled previewVideoProvider previewYoutubeEmbedUrl previewVideoTitle previewVideoDurationSeconds",
  ],
  ["Schedule", "format startAt endAt enrollmentCloseAt timeZone capacity"],
  [
    "Presentation",
    "publicTheme visualTemplate accentColor themeLabel primaryCtaLabel primaryCtaPath secondaryCtaLabel secondaryCtaPath",
  ],
  ["Offer settings — inactive until enrollment is enabled", "offer"],
];
const PRESENTATION_COPY = {
  publicTheme: { label: "Presentation preset", help: "Choose the shared course design. Each preset uses the website’s current design and applies to all existing and future courses that select it. This does not change course content or enrollment." },
  visualTemplate: { label: "Detail signal text preset", help: "Controls the text shown in the detail signal strip. The Presentation preset controls the visual treatment; this field does not select a different page layout." },
  accentColor: { label: "Accent color override", help: "Optional #RRGGBB color. Leave empty to use the selected preset’s default accent." },
  themeLabel: { label: "Card presentation label", help: "Optional label used by course cards. The course-page heading uses the selected category." },
};
const templateChoices = Object.entries(COURSE_TEMPLATE_NAMES).map(([id, name]) => ({ id, name }));
const presentationChoices = { publicTheme: templateChoices, visualTemplate: [{ id: 'course-detail', name: 'Course detail' }, ...templateChoices] };
const COMMON = [
  ["Search metadata", "seoTitle metaDescription"],
  [
    "Publication and homepage placement",
    "status homepageVisible homepageOrder featured sortOrder",
  ],
];
const MEDIA = {
  speakers: [["headshot", "Portrait", "headshotUrl", "portraitAlt", "image/"]],
  "course-library": [
    ["hero", "Course image", "heroImageUrl", "heroImageAlt", "image/"],
    [
      "featured",
      "Card image",
      "featuredImageUrl",
      "featuredImageAlt",
      "image/",
    ],
    [
      "previewCover",
      "Preview cover",
      "previewCoverImageUrl",
      "previewCoverImageAlt",
      "image/",
    ],
    ["previewVideo", "Public preview video", "previewVideoUrl", null, "video/"],
  ],
};
function Group({ title, names, schema, value, setValue, choices, fieldCopy, help, children }) {
  const keys = names.split(" ");
  return (
    <fieldset className="aw-array" aria-describedby={help ? `aw-${title.replace(/\W/g, "")}-help` : undefined}>
      <legend>{title}</legend>
      {help && <p id={`aw-${title.replace(/\W/g, "")}-help`} className="aw-muted">{help}</p>}
      <Fields
        schema={{
          ...schema,
          properties: Object.fromEntries(
            keys
              .filter((key) => schema.properties[key])
              .map((key) => [key, schema.properties[key]]),
          ),
        }}
        value={value}
        onChange={setValue}
        choices={choices}
        fieldCopy={fieldCopy}
        prefix={title.replace(/\W/g, "")}
        reorder
      />
      {children}
    </fieldset>
  );
}
function References({
  title,
  records,
  ids = [],
  onChange,
  excluded = [],
  currentId,
}) {
  const [selected, setSelected] = useState("");
  return (
    <fieldset className="aw-array">
      <legend>{title}</legend>
      <ol>
        {ids.map((id, index) => {
          const row = records.find((record) => record.id === id);
          return (
            <li className="aw-array-row" key={id}>
              <span>
                {row?.name || row?.title || id}{" "}
                {row ? `(${row.status})` : "(unavailable)"}
              </span>
              <div className="aw-row">
                {[-1, 1].map((step) => (
                  <button
                    type="button"
                    className="aw-button aw-small"
                    key={step}
                    aria-label={`Move ${title.toLowerCase()} ${index + 1} ${step < 0 ? "up" : "down"}`}
                    disabled={index + step < 0 || index + step >= ids.length}
                    onClick={() => {
                      const next = [...ids];
                      [next[index], next[index + step]] = [
                        next[index + step],
                        next[index],
                      ];
                      onChange(next);
                    }}
                  >
                    {step < 0 ? "Move up" : "Move down"}
                  </button>
                ))}
                <button
                  type="button"
                  className="aw-button aw-small"
                  onClick={() => onChange(ids.filter((item) => item !== id))}
                >
                  Remove {row?.name || row?.title || id}
                </button>
              </div>
            </li>
          );
        })}
      </ol>
      <label>
        Add {title.toLowerCase()}
        <select
          aria-label={`Add ${title.toLowerCase()}`}
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">Select a published record</option>
          {records
            .filter(
              (row) =>
                row.status === "published" &&
                row.id !== currentId &&
                !ids.includes(row.id) &&
                !excluded.includes(row.id),
            )
            .map((row) => (
              <option key={row.id} value={row.id}>
                {row.name || row.title}
              </option>
            ))}
        </select>
      </label>
      <button
        type="button"
        className="aw-button"
        disabled={!selected || ids.includes(selected) || ids.length >= 50}
        onClick={() => {
          onChange([...ids, selected]);
          setSelected("");
        }}
      >
        Add selection
      </button>
    </fieldset>
  );
}
export default function ContentEditor({ domain, id, record, onRefresh }) {
  const profile = useAdmin(),
    navigate = useNavigate();
  const schemaState = useRemote(`/admin/editor-schema/${domain}`);
  const [target] = useState(id || crypto.randomUUID()),
    [tick, setTick] = useState(0),
    [withdraw, setWithdraw] = useState(false),
    [uploadedId, setUploadedId] = useState(null),
    [publishedUpload, setPublishedUpload] = useState(null);
  const uploadState = useRemote(
    uploadedId ? `/admin/versions/${uploadedId}` : null,
    tick,
  );
  const media = useContentRecords("media", tick),
    speakers = useContentRecords("speakers", tick),
    courses = useContentRecords("course-library", tick);
  const linkedCourses = courses.data.filter((course) =>
    course.speakerIds?.includes(id),
  );
  const linkedBundles = courses.data.filter((course) =>
    course.bundleCourseIds?.includes(id),
  );
  const editable = hasCapability(profile, "content.edit");
  if (!editable)
    return (
      <section className="aw-panel">
        <Record data={record} />
        <EffectiveRecordMedia domain={domain} id={id} revision={record?.revision} />
        <p>You have read-only access.</p>
      </section>
    );
  return (
    <Loading state={schemaState}>
      <EffectiveRecordMedia domain={domain} id={id} revision={record?.revision} />
      {schemaState.data && (
        <>
      {domain === 'course-library' && id && <Link to={'/admin/course-delivery/'+id}>Manage enrollment and private delivery</Link>}
          {id && (
            <div className="aw-row">
              <Link className="aw-button" to={`/admin/${domain}/${id}/preview`}>
                Preview saved content
              </Link>
              {record.status === "published" && (
                <Link
                  className="aw-link"
                  to={`/${domain === "speakers" ? "speakers" : "webinars"}/${record.slug}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View published page
                </Link>
              )}
            </div>
          )}
          <ActionForm
            title={id ? "Edit content" : "Create content"}
            schema={schemaState.data}
            initial={{
              status: "draft",
              homepageVisible: false,
              homepageOrder: 0,
              sortOrder: 0,
              ...(domain === "course-library"
                ? {
                    category: "training",
                    kind: "course",
                    isDemo: true,
                    modules: [],
                    speakerIds: [],
                    bundleCourseIds: [],
                    format: "outline",
                    offer: { mode: "demo", currency: "USD" },
                    enrollmentEnabled: false,
                    paymentRequired: false,
                    paymentAmount: null,
                  }
                : {}),
              ...cleanDraft(schemaState.data, record || {}),
              revision: record?.revision || 0,
            }}
            description="Save changes before previewing. Publishing updates website records; it does not deploy the website."
            renderFields={(value, setValue) => (
              <>
                {(domain === "speakers" ? SPEAKER_GROUPS : COURSE_GROUPS).map(
                  ([title, names]) => (
                    <Group
                      key={title}
                      title={title}
                      names={names}
                      choices={domain === 'course-library' && title === 'Presentation' ? presentationChoices : undefined}
                      fieldCopy={domain === "speakers" && title === "Identity" ? { displayName: { label: "Display name" } } : domain === "course-library" && title === "Presentation" ? PRESENTATION_COPY : undefined}
                      help={domain === "course-library" && title === "Presentation" ? "Save changes, then use Preview saved content. Preview shows the saved course detail." : undefined}
                      schema={schemaState.data}
                      value={value}
                      setValue={setValue}
                    >
                      {domain === 'course-library' && title === 'Presentation' && <CourseThemePreview record={value} />}
                    </Group>
                  ),
                )}
                {domain === "course-library" && (
                  <Loading state={speakers}>
                    <References
                      title="Speakers"
                      records={speakers.data}
                      ids={value.speakerIds}
                      onChange={(ids) =>
                        setValue({
                          ...value,
                          speakerIds: ids,
                          primarySpeakerId: ids.includes(value.primarySpeakerId)
                            ? value.primarySpeakerId
                            : "",
                        })
                      }
                    />
                    <label>
                      Primary speaker
                      <select
                        aria-label="Primary speaker"
                        value={value.primarySpeakerId || ""}
                        onChange={(e) =>
                          setValue({
                            ...value,
                            primarySpeakerId: e.target.value,
                          })
                        }
                      >
                        <option value="">First selected speaker</option>
                        {(value.speakerIds || []).map((id) => (
                          <option key={id} value={id}>
                            {speakers.data.find((row) => row.id === id)?.name ||
                              id}
                          </option>
                        ))}
                      </select>
                    </label>
                  </Loading>
                )}
                {domain === "course-library" &&
                  (value.kind === "bundle" ||
                    value.bundleCourseIds?.length > 0) && (
                    <Loading state={courses}>
                      <References
                        title="Bundle courses"
                        records={courses.data}
                        ids={value.bundleCourseIds}
                        excluded={courses.data
                          .filter((row) => row.kind === "bundle")
                          .map((row) => row.id)}
                        currentId={target}
                        onChange={(ids) =>
                          setValue({ ...value, bundleCourseIds: ids })
                        }
                      />
                    </Loading>
                  )}
                <fieldset className="aw-array">
                  <legend>Public media</legend>
                  <p className="aw-muted">
                    Choose a checked, published asset. Upload new files below;
                    use Public media to publish them after validation, then
                    refresh the choices.
                  </p>
                  <Loading state={media}>
                    {MEDIA[domain].map(([key, title, url, alt, type]) => {
                      const ref = value.mediaRefs?.[key] || "";
                      const options = media.data.filter(
                        (asset) =>
                          asset.status === "published" &&
                          asset.public === true &&
                          asset.accessScope === "public_media" &&
                          asset.premiumContentAllowed === false &&
                          asset.contentType?.startsWith(type),
                      );
                      return (
                        <div className="aw-array-row" key={key}>
                          <label>
                            {title}
                            <select
                              aria-label={title}
                              value={ref}
                              onChange={(e) => {
                                const asset = options.find(
                                  (row) => row.id === e.target.value,
                                );
                                setValue({
                                  ...value,
                                  mediaRefs: {
                                    ...value.mediaRefs,
                                    [key]: e.target.value,
                                  },
                                  [url]: asset?.url || "",
                                  ...(alt && asset
                                    ? {
                                        [alt]:
                                          asset.altText || value[alt] || "",
                                      }
                                    : {}),
                                });
                              }}
                            >
                              <option value="">
                                {value[url] && !ref
                                  ? "Current existing artwork"
                                  : "No asset selected"}
                              </option>
                              {ref &&
                                !options.some((row) => row.id === ref) && (
                                  <option value={ref}>
                                    Current asset (not ready): {ref}
                                  </option>
                                )}
                              {options.map((asset) => (
                                <option key={asset.id} value={asset.id}>
                                  {asset.name || asset.id}
                                </option>
                              ))}
                            </select>
                          </label>
                          {value[url] && type === "image/" && (
                            <img
                              src={value[url]}
                              alt={value[alt] || title}
                              style={{
                                maxWidth: 160,
                                maxHeight: 200,
                                objectFit: "contain",
                              }}
                            />
                          )}
                          {value[url] && type === "video/" && (
                            <video
                              src={value[url]}
                              controls
                              preload="metadata"
                              style={{ maxWidth: "100%", width: 320 }}
                            />
                          )}
                          <button
                            type="button"
                            className="aw-button aw-small"
                            onClick={() =>
                              setValue({
                                ...value,
                                mediaRefs: { ...value.mediaRefs, [key]: "" },
                                [url]: "",
                              })
                            }
                          >
                            Clear {title.toLowerCase()}
                          </button>
                          {alt && (
                            <label>
                              {label(alt)}
                              <input
                                value={value[alt] || ""}
                                maxLength={6000}
                                onChange={(e) =>
                                  setValue({ ...value, [alt]: e.target.value })
                                }
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </Loading>
                  <button
                    type="button"
                    className="aw-button"
                    onClick={() => setTick((n) => n + 1)}
                  >
                    Refresh media and relationships
                  </button>
                </fieldset>
                {COMMON.map(([title, names]) => (
                  <Group
                    key={title}
                    title={title}
                    names={names}
                    schema={schemaState.data}
                    value={value}
                    setValue={setValue}
                    choices={{
                      status: schemaState.data.properties.status.enum
                        .filter(
                          (status) =>
                            status !== "published" ||
                            hasCapability(profile, "content.publish"),
                        )
                        .map((status) => ({ id: status, name: status })),
                    }}
                  />
                ))}
                {record?.status === "published" &&
                  value.status !== "published" && (
                    <div className="aw-notice">
                      <p>
                        This removes the{" "}
                        {domain === "speakers"
                          ? "speaker profile and its course instructor references"
                          : "course from public listings and bundle references"}
                        .{" "}
                        {domain === "speakers"
                          ? linkedCourses.length
                          : linkedBundles.length}{" "}
                        linked {domain === "speakers" ? "courses" : "bundles"}{" "}
                        currently reference it.
                      </p>
                      <Loading state={courses}>
                        <ul>
                          {(domain === "speakers"
                            ? linkedCourses
                            : linkedBundles
                          ).map((row) => (
                            <li key={row.id}>{row.title}</li>
                          ))}
                        </ul>
                      </Loading>
                      <label className="aw-check">
                        <input
                          type="checkbox"
                          disabled={courses.loading || !!courses.error}
                          checked={withdraw}
                          onChange={(e) => setWithdraw(e.target.checked)}
                        />
                        I understand the effect of unpublishing this content.
                      </label>
                    </div>
                  )}
              </>
            )}
            submit={(content, key, baseline) => {
              if (
                record?.status === "published" &&
                content.status !== "published" &&
                (!withdraw || courses.loading || courses.error)
              )
                throw Error(
                  "Review linked records and confirm unpublishing before saving.",
                );
              if (domain === "course-library" && content.offer)
                content.offer = { ...content.offer, currency: "USD" };
              return adminRequest(`/admin/cms/${domain}/${target}`, {
                method: "PUT",
                key,
                body: { revision: baseline?.revision || 0, content },
              });
            }}
            onDone={(result) => {
              if (domain === 'course-library') notifyCourseContentChanged();
              if (!id) navigate(`/admin/${domain}/${target}`);
              else onRefresh(result);
            }}
          />
          {domain === "speakers" && id && (
            <section className="aw-panel">
              <h2>Associated courses</h2>
              <Loading state={courses}>
                {linkedCourses.length ? (
                  <ul>
                    {linkedCourses.map((course) => (
                      <li key={course.id}>
                        <Link
                          className="aw-link"
                          to={`/admin/course-library/${course.id}`}
                        >
                          {course.title}
                        </Link>{" "}
                        ·{" "}
                        {course.primarySpeakerId === id
                          ? "Primary speaker"
                          : "Assigned speaker"}{" "}
                        · {course.status}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>
                    No courses assigned. Assign this speaker from the Course
                    library.
                  </p>
                )}
              </Loading>
            </section>
          )}
          {hasCapability(profile, "resources.manage") && (
            <>
              <Upload
                media
                statusInNewTab
                onDone={(uploadId) => {
                  setUploadedId(uploadId);
                  setPublishedUpload(null);
                  setTick((n) => n + 1);
                }}
              />
              {uploadedId && (
                <section className="aw-panel">
                  <h2>Uploaded media status</h2>
                  <Loading state={uploadState}>
                    <p>
                      {uploadState.data?.status === "ready"
                        ? "Validation passed. Publish this asset, refresh the media choices above, then select it and save the content."
                        : `Current status: ${uploadState.data?.status || "checking"}`}
                    </p>
                    {publishedUpload === uploadedId && (
                      <p role="status">
                        Media published. Select it in the content form above and
                        save.
                      </p>
                    )}
                    {uploadState.data?.status === "ready" &&
                      publishedUpload !== uploadedId && (
                        <SimpleAction
                          title="Publish checked public media"
                          action={() =>
                            adminRequest(`/admin/media/${uploadedId}/publish`, {
                              method: "POST",
                              body: {},
                            })
                          }
                          onDone={() => {
                            setPublishedUpload(uploadedId);
                            setTick((n) => n + 1);
                          }}
                        />
                      )}
                  </Loading>
                  <button
                    type="button"
                    className="aw-button"
                    onClick={() => setTick((n) => n + 1)}
                  >
                    Check upload status
                  </button>
                </section>
              )}
              <Link
                className="aw-link"
                target="_blank"
                rel="noreferrer"
                to="/admin/media"
              >
                Open Public media to check and publish uploads
              </Link>
            </>
          )}
          {id && (
            <RelatedRecords
              title="Change history"
              path={`/admin/content-history/${domain}/${id}`}
              refresh={record?.revision}
            />
          )}
        </>
      )}
    </Loading>
  );
}
